import { useEffect, useState } from 'react';
import { getClientId } from './helpers';

export interface ApplicationBranding {
  name: string | null;
  title: string | null;
  logoUrl: string | null;
}

/**
 * Fetches per-application branding (logoUrl/title) for the current OAuth client,
 * based on the `client_id` query param. Returns `null` until/unless branding is
 * available, so callers fall back to the default domain theme.
 */
export function useApplicationBranding(): ApplicationBranding | null {
  const [branding, setBranding] = useState<ApplicationBranding | null>(null);

  useEffect(() => {
    const clientId = getClientId();
    if (!clientId) {
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `/api/application/branding?clientId=${encodeURIComponent(clientId)}`,
        );
        if (!res.ok) {
          return;
        }
        const data = await res.json();
        if (!cancelled && data && data.error === false && data.application) {
          setBranding({
            name: data.application.name ?? null,
            title: data.application.title ?? null,
            logoUrl: data.application.logoUrl ?? null,
          });
        }
      } catch {
        // network/parse error - silently fall back to the default theme.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return branding;
}
